import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from '../bundles/auth/providers/AuthProvider';
import { CsrfContext } from './CsrfProvider';
import { listUserGroups, createGroup as createGroupService } from '../services/group/Groups';

const GroupContext = createContext();

export const useGroup = () => {
    const context = useContext(GroupContext);
    if (!context) {
        throw new Error('useGroup must be used within a GroupProvider');
    }
    return context;
};

export const GroupProvider = ({ children }) => {
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const lastUserIdRef = useRef(undefined);

    // Initialize from localStorage so dashboard can run immediately with correct group_id (no wait for groups).
    const [selectedGroupId, setSelectedGroupId] = useState(() => {
        try {
            const s = localStorage.getItem('selectedGroupId');
            const result = s === 'all' ? 'all' : (s && s !== '' ? s : null);
            console.log('[GroupProvider] localStorage PULL (init)', { raw: s ?? '(empty/missing)', selectedGroupId: result });
            return result;
        } catch (e) {
            console.log('[GroupProvider] localStorage PULL (init)', { error: e?.message, selectedGroupId: null });
            return null;
        }
    });
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [favoriteGroupId, setFavoriteGroupId] = useState(null);
    const [hydratedBills, setHydratedBills] = useState([]);
    const [billsGroupId, setBillsGroupId] = useState(null);

    // Load groups when logged in (csrfToken + authClaims). Skip GET /api/groups?bills=true when logged out.
    useEffect(() => {
        const hasCsrf = !!csrfToken;
        const userId = authClaims?.user_sub ?? authClaims?.sub ?? null;
        const hasUserId = !!userId;
        console.log('[GroupProvider] effect', { hasCsrf, hasUserId, lastUserId: lastUserIdRef.current ?? null });
        if (!csrfToken) {
            lastUserIdRef.current = undefined;
            setLoading(false);
            return;
        }
        if (!userId) {
            // Only clear selectedGroupId when actually logging out (had user, now don't). Preserve value from localStorage when auth not yet ready.
            const wasLoggedIn = lastUserIdRef.current != null;
            if (wasLoggedIn) {
                console.log('[GroupProvider] clearing selectedGroupId (logout)');
                setSelectedGroupId(null);
            }
            lastUserIdRef.current = undefined;
            setGroups([]);
            setHydratedBills([]);
            setFavoriteGroupId(null);
            setBillsGroupId(null);
            setLoading(false);
            return;
        }
        if (userId !== lastUserIdRef.current) {
            lastUserIdRef.current = userId;
            console.log('[GroupProvider] calling loadGroups()');
            loadGroups();
        }
    }, [authClaims, csrfToken]);

    const loadGroups = async () => {
        try {
            setLoading(true);
            const data = await listUserGroups(csrfToken, { bills: true });
            const result = data?.result ?? {};
            const groupsData = result.groups ?? [];
            const favoriteId = result.favorite_group_id ?? null;
            setGroups(groupsData);
            setFavoriteGroupId(favoriteId || null);
            setHydratedBills(result.bills ?? []);
            const primaryGroupId = result.bills_group_id ?? favoriteId ?? null;
            setBillsGroupId(primaryGroupId);

            // Restore last-viewed group from localStorage so it persists across refreshes.
            let initialSelected = primaryGroupId != null && primaryGroupId !== '' ? primaryGroupId : null;
            try {
                const stored = localStorage.getItem('selectedGroupId');
                if (stored === 'all') {
                    initialSelected = 'all';
                } else if (stored === '' || stored == null) {
                    initialSelected = primaryGroupId != null && primaryGroupId !== '' ? primaryGroupId : null;
                } else if (stored) {
                    // Stored UUID: use only if it's still a valid group
                    const exists = groupsData.some((g) => g && String(g.id) === String(stored));
                    initialSelected = exists ? stored : initialSelected;
                }
                console.log('[GroupProvider] localStorage PULL (after groups)', {
                    raw: stored ?? '(empty/missing)',
                    primaryGroupId,
                    selectedGroupId: initialSelected,
                    storedValid: stored && groupsData.some((g) => g && String(g.id) === String(stored)),
                });
            } catch (e) {
                console.log('[GroupProvider] localStorage PULL (after groups)', { error: e?.message });
            }
            setSelectedGroupId(initialSelected);
            // Persist so next refresh has it from init (avoids group_id=all then second request).
            try {
                if (initialSelected === 'all') {
                    localStorage.setItem('selectedGroupId', 'all');
                } else if (initialSelected != null && initialSelected !== '') {
                    localStorage.setItem('selectedGroupId', String(initialSelected));
                } else {
                    localStorage.setItem('selectedGroupId', '');
                }
            } catch {}
        } catch (error) {
            console.error('Error loading groups:', error);
        } finally {
            setLoading(false);
        }
    };

    const setSelectedGroup = useCallback((groupId) => {
        setSelectedGroupId(groupId);
        const value = groupId === 'all' ? 'all' : (groupId ? String(groupId) : '');
        if (groupId === 'all') {
            localStorage.setItem('selectedGroupId', 'all');
        } else if (groupId) {
            localStorage.setItem('selectedGroupId', String(groupId));
        } else {
            localStorage.setItem('selectedGroupId', '');
        }
        console.log('[GroupProvider] localStorage PUSH', { groupId, value });
    }, []);

    const refreshGroups = useCallback(async () => {
        await loadGroups();
    }, [authClaims, csrfToken]);

    const createGroupAndRefresh = useCallback(async (name) => {
        if (!csrfToken) return null;
        try {
            const newGroup = await createGroupService(name, csrfToken);
            await refreshGroups();
            return newGroup;
        } catch (error) {
            console.error('Error creating group:', error);
            throw error;
        }
    }, [csrfToken, refreshGroups]);

    const value = {
        selectedGroupId,
        setSelectedGroup,
        groups,
        loading,
        refreshGroups,
        createGroup: createGroupAndRefresh,
        favoriteGroupId: favoriteGroupId ?? null,
        hydratedBills,
        billsGroupId: billsGroupId ?? null,
    };

    return (
        <GroupContext.Provider value={value}>
            {children}
        </GroupContext.Provider>
    );
};
